/**
 * ClarityV2Context.jsx - Clarity Mode v2 State Management
 * 
 * Provides ambient simplification state across the application.
 * Handles user preferences, behavioral triggers, and concept tracking.
 * 
 * GOVERNANCE BOUNDARY:
 * - Clarity explains, never decides
 * - No recommendations, no approvals
 * - Navigation guidance only
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { isClarityV2Enabled } from '../lib/config/featureFlags';

// Storage keys
const STORAGE_KEYS = {
  PREFERENCE: 'granullo_clarity_v2_preference',
  SEEN_CONCEPTS: 'granullo_clarity_v2_seen_concepts',
  PROMPT_COUNT: 'granullo_clarity_v2_prompt_count',
  SESSION_DISMISSED: 'granullo_clarity_v2_session_dismissed'
};

// Preference values
export const CLARITY_PREFERENCE = {
  ENABLED: 'enabled',
  DISABLED: 'disabled',
  LATER: 'later'
};

// Context
const ClarityV2Context = createContext(null);

/**
 * ClarityV2Provider - Provides Clarity state to the app
 */
export const ClarityV2Provider = ({ children }) => {
  // Check feature flag
  const featureEnabled = isClarityV2Enabled();

  // User preference state
  const [preference, setPreferenceState] = useState(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEYS.PREFERENCE);
  });

  // Seen concepts tracking
  const [seenConcepts, setSeenConceptsState] = useState(() => {
    if (typeof window === 'undefined') return [];
    const stored = localStorage.getItem(STORAGE_KEYS.SEEN_CONCEPTS);
    return stored ? JSON.parse(stored) : [];
  });

  // Prompt count (for "later" re-prompting)
  const [promptCount, setPromptCountState] = useState(() => {
    if (typeof window === 'undefined') return 0;
    return parseInt(localStorage.getItem(STORAGE_KEYS.PROMPT_COUNT) || '0', 10);
  });

  // Session-level dismissal
  const [sessionDismissed, setSessionDismissed] = useState(false);

  // Derived: Is Clarity active?
  const isActive = useMemo(() => {
    if (!featureEnabled) return false;
    return preference === CLARITY_PREFERENCE.ENABLED;
  }, [featureEnabled, preference]);

  // Derived: Should show prompt?
  const shouldShowPrompt = useMemo(() => {
    if (!featureEnabled) return false;
    if (sessionDismissed) return false;
    if (preference === CLARITY_PREFERENCE.ENABLED) return false;
    if (preference === CLARITY_PREFERENCE.DISABLED) return false;
    if (preference === CLARITY_PREFERENCE.LATER && promptCount < 3) return false;
    return true;
  }, [featureEnabled, sessionDismissed, preference, promptCount]);

  // Set preference
  const setPreference = useCallback((value) => {
    setPreferenceState(value);
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEYS.PREFERENCE, value);
      if (value === CLARITY_PREFERENCE.LATER) {
        localStorage.setItem(STORAGE_KEYS.PROMPT_COUNT, '0');
        setPromptCountState(0);
      }
    }
  }, []);

  // Dismiss for session
  const dismissForSession = useCallback(() => {
    setSessionDismissed(true);
  }, []);

  // Increment prompt count (called on navigation)
  const incrementPromptCount = useCallback(() => {
    if (preference === CLARITY_PREFERENCE.LATER) {
      const newCount = promptCount + 1;
      setPromptCountState(newCount);
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEYS.PROMPT_COUNT, String(newCount));
      }
    }
  }, [preference, promptCount]);

  // Mark concept as seen
  const markConceptSeen = useCallback((conceptId) => {
    if (!seenConcepts.includes(conceptId)) {
      const updated = [...seenConcepts, conceptId];
      setSeenConceptsState(updated);
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEYS.SEEN_CONCEPTS, JSON.stringify(updated));
      }
    }
  }, [seenConcepts]);

  // Check if concept is new
  const isConceptNew = useCallback((conceptId) => {
    return !seenConcepts.includes(conceptId);
  }, [seenConcepts]);

  // Toggle Clarity
  const toggle = useCallback(() => {
    const newValue = isActive 
      ? CLARITY_PREFERENCE.DISABLED 
      : CLARITY_PREFERENCE.ENABLED;
    setPreference(newValue);
  }, [isActive, setPreference]);

  // Context value
  const value = useMemo(() => ({
    // State
    isActive,
    preference,
    shouldShowPrompt,
    featureEnabled,
    seenConcepts,
    
    // Actions
    setPreference,
    dismissForSession,
    incrementPromptCount,
    markConceptSeen,
    isConceptNew,
    toggle,
    
    // Constants
    CLARITY_PREFERENCE
  }), [
    isActive,
    preference,
    shouldShowPrompt,
    featureEnabled,
    seenConcepts,
    setPreference,
    dismissForSession,
    incrementPromptCount,
    markConceptSeen,
    isConceptNew,
    toggle
  ]);

  return (
    <ClarityV2Context.Provider value={value}>
      {children}
    </ClarityV2Context.Provider>
  );
};

/**
 * useClarityV2 - Hook to access Clarity state
 */
export const useClarityV2 = () => {
  const context = useContext(ClarityV2Context);
  if (!context) {
    throw new Error('useClarityV2 must be used within ClarityV2Provider');
  }
  return context;
};

export default ClarityV2Context;
